import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridSortModel,
} from "@mui/x-data-grid-premium";
import { TransportOrderNotificationListItemDto, TransportOrderNotificationState } from "../../models/transport/transportOrderNotification";
import { getDateTime } from "../transport/components/transportUtils";
import { MuiDataGrid } from "../framework/muiDataGrid";
import { transportOrderStateNames } from "../../models/transport/transportOrder";
import { CustomPagination } from "../framework/editableDataGrid/customPagination";
import { useAppSelector } from "../../framework/customStore";
import { confirmAllMobileToDesktopNotifications } from "../../services/transportOrderService";
import { showError } from "../storage/storageUtils";
import { PagedResult } from "../../models/pagedResult/pagedResult";
import { Base } from "../../framework/base";

interface NotificationsTableProps {
    notifications: TransportOrderNotificationListItemDto[];
    currentPage: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    setPreviousNotificationsCount: (pageSize: number) => void;
    fetchNotifications: (sortField?: string, sortOrder?: string) =>
        Promise<PagedResult<TransportOrderNotificationListItemDto>>;
}

export const NotificationsTable = ({
    notifications,
    currentPage,
    pageSize,
    totalCount,
    onPageChange,
    onPageSizeChange,
    setPreviousNotificationsCount,
    fetchNotifications,
}: NotificationsTableProps) => {
    const { t } = useTranslation();

    const [confirmDisabled, setConfirmDisabled] = useState(false);
    const employees = useAppSelector((state) => state.employees.employees);

    // Convert our pagination to MUI DataGrid format
    const paginationModel = useMemo(() => ({
        page: currentPage - 1, // MUI uses 0-based indexing
        pageSize: pageSize
    }), [currentPage, pageSize]);

    const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
        onPageChange(model.page + 1); // Convert back to 1-based indexing
        onPageSizeChange(model.pageSize);
    }, [onPageChange, onPageSizeChange]);

    const handleConfirm = async() => {
        setConfirmDisabled(true);
        try {
            await confirmAllMobileToDesktopNotifications();
            setPreviousNotificationsCount(0);

            fetchNotifications()
                .catch(() => {
                    showError(t("notifications.fetchFailed"));
                })
                .finally(() => {
                    setConfirmDisabled(false);
                });
        } catch (error) {
            setConfirmDisabled(false);
            console.error("Error confirming notifications!:", error);
        }
    };

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        if (sortModel[0]) {
            fetchNotifications(
                Base.capitalizeFirstChar(sortModel[0].field),
                sortModel[0].sort
            )
                .catch(() => {
                    showError(t("notifications.fetchFailed"));
                });
        } else {
            fetchNotifications()
                .catch(() => {
                    showError(t("notifications.fetchFailed"));
                });
        }
    }, []);

    const getStateText = (state: number) => {
        if (state < TransportOrderNotificationState.Confirmed.valueOf()) {
            return t("notifications.unconfirmed");
        }
        if (state === TransportOrderNotificationState.Confirmed.valueOf()) {
            return t("notifications.confirmed");
        }
        return "-";
    };

    const getEmployeeName = (id: string) => {
        const employee = employees.find(i => i.id === id);
        if (employee) {
            return employee.fullName;
        }

        return "-";
    };

    const gridDef: GridColDef<TransportOrderNotificationListItemDto>[] = useMemo(
        () => [
            {
                headerName: t("common.created"),
                field: "createdDateTime",
                width: 150,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {getDateTime(params.row?.createdDateTime)}
                        </div>
                    );
                },
            },
            {
                headerName: t("transport.order.creator"),
                field: "createdEmployeeId",
                sortable: false,
                type: "string",
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {getEmployeeName(params.row?.createdEmployeeId)}
                        </div>
                    );
                },
            },
            {
                headerName: t("common.message"),
                field: "message",
                sortable: false,
                type: "string",
                flex: 4,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {t(params.row?.message, {
                                orderNro: params.row?.transportOrderNumber,
                                vehicleName: params.row?.vehicleRegisterNumber,
                                state: t(transportOrderStateNames[params.row?.orderState]),
                                employeeName: getEmployeeName(params.row?.createdEmployeeId),
                            })}
                        </div>
                    );
                },
            },
            {
                headerName: t("common.status"),
                field: "state",
                sortable: false,
                type: "string",
                flex: 1,
                renderCell: (
                    params: GridRenderCellParams<TransportOrderNotificationListItemDto>
                ) => {
                    return (
                        <div>
                            {getStateText(params.row?.state)}
                        </div>
                    );
                },
            },
        ],
        [employees]
    );

    return (
        <>
            <MuiDataGrid
                rows={notifications}
                columns={gridDef}
                density="compact"
                rowSelection={false}
                disableColumnFilter
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                rowCount={totalCount}
                pageSizeOptions={[25, 50, 100]}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                customPagination={notifications?.length > 0 ? CustomPagination : null}
                toolbarProps={{
                    title: t("notifications.masterList.tableHeader"),
                    enableColumnSelector: false,
                    disableQuickFilter: true,
                    disableToolbarColumnsButton: true,
                    customToolbarButtons: notifications?.length > 0 ? [
                        {
                            key: "confirm-all",
                            color: "success",
                            variant: "contained",
                            onClick: handleConfirm,
                            children: t("notifications.confirmAll"),
                            disabled: confirmDisabled,
                            sx: { marginRight: 1 }
                        },
                    ] : null
                }}
            />
        </>
    );
};
